/**
 * LuckyOrangeProvider
 * ───────────────────
 * React Context provider that manages the Lucky Orange analytics lifecycle.
 *
 * Responsibilities:
 *  1. Injects the Lucky Orange CDN <script> tag once per page load.
 *  2. Exposes a stable API for session identification and custom event tracking
 *     that is safe to call at any component lifecycle stage — before or after
 *     the async script has finished loading — via the LOQ command-queue pattern.
 *  3. Provides a `useLuckyOrange` hook for clean, tree-shaken consumption
 *     throughout the application without prop-drilling.
 *
 * Environment variable:
 *  REACT_APP_LUCKY_ORANGE_SITE_ID — your Lucky Orange site/property ID.
 *  Leave unset (or set disabled={true}) to suppress tracking in local dev.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { LOInstance } from '../types/luckyOrange';

// ─────────────────────────────────────────────────────────────────────────────
// Milestone / event-name types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strongly-typed preset milestone names for the application journey.
 * Add new entries here as the product evolves; the compiler will surface
 * all call sites that need updating.
 */
export type Milestone =
  // ── Job Application flow ───────────────────────────────────────────────────
  | 'Application Started'
  | 'Step Viewed'
  | 'Step Completed'
  | 'Validation Error'
  | 'Application Submitted'
  | 'Application Abandoned'
  | 'Resume Uploaded'
  // ── Employee Packet flow ───────────────────────────────────────────────────
  | 'Employee Packet Started'
  | 'Employee Packet Step Completed'
  | 'Employee Packet Submitted'
  // ── HR / Admin actions ─────────────────────────────────────────────────────
  | 'Application Viewed'
  | 'Application Approved'
  | 'Application Rejected';

/**
 * Accepts any known Milestone name (with IDE autocomplete) or any other
 * string for ad-hoc events — without sacrificing type safety for the
 * preset names.
 */
export type EventName = Milestone | (string & {});

// ─────────────────────────────────────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────────────────────────────────────

export interface LuckyOrangeContextValue {
  /**
   * True once the LO script has initialised and its visitor / events APIs
   * are available. Most callers do NOT need to check this flag because
   * `trackMilestone` and `identifyApplication` both use the LOQ queue
   * and are safe to call before `isReady` becomes true.
   */
  isReady: boolean;

  /**
   * Associates the current Lucky Orange session recording with an internal
   * Application ID so HR staff can pull up the exact session from a database
   * record without exposing any applicant PII.
   *
   * SECURITY NOTE: Pass ONLY the internal database ID (GUID / numeric ID).
   * NEVER pass the applicant's name, email, SSN, phone number, or any other
   * personally identifiable information into this method.
   *
   * @param applicationId  Internal database identifier (non-PII).
   */
  identifyApplication: (applicationId: string) => void;

  /**
   * Fires a named milestone event in Lucky Orange.
   *
   * The method uses the LOQ command-queue pattern so it is always safe to
   * call — even if the LO script has not yet finished loading.  Lucky Orange
   * will replay the command immediately upon script initialisation.
   *
   * @param eventName  A Milestone preset name or any custom string.
   * @param metadata   Optional non-PII payload (step index, field names, etc.).
   *                   Never include personal data in metadata.
   */
  trackMilestone: (eventName: EventName, metadata?: Record<string, unknown>) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context object
// ─────────────────────────────────────────────────────────────────────────────

const LuckyOrangeContext = createContext<LuckyOrangeContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export interface LuckyOrangeProviderProps {
  /**
   * Lucky Orange site ID.
   * Defaults to process.env.REACT_APP_LUCKY_ORANGE_SITE_ID.
   * Explicit prop is useful for testing or multi-tenancy scenarios.
   */
  siteId?: string;

  /**
   * Set to true to prevent the script from being injected.
   * Useful for local development or test environments where you don't
   * want real sessions tracked.
   *
   * @default false
   */
  disabled?: boolean;

  children: ReactNode;
}

export const LuckyOrangeProvider: React.FC<LuckyOrangeProviderProps> = ({
  siteId = process.env.REACT_APP_LUCKY_ORANGE_SITE_ID,
  disabled = false,
  children,
}) => {
  const [isReady, setIsReady] = useState(false);

  /**
   * Ref to the live LO instance.
   * We keep this alongside the isReady flag so that helpers that need
   * a synchronous call path can use it directly after the script loads.
   * Outside consumers should always go through the context methods, not
   * this ref.
   */
  const loRef = useRef<LOInstance | undefined>(undefined);

  /**
   * Stores the most recent Application ID passed to identifyApplication.
   * Using a ref (not state) means updates are invisible to React's render
   * cycle — we never want a re-render just because the ID changed — while
   * still giving trackMilestone access to the latest value via closure.
   */
  const currentApplicationIdRef = useRef<string>('');

  // ── Script injection ────────────────────────────────────────────────────────
  useEffect(() => {
    // Guard 1: skip if tracking is explicitly disabled (e.g. dev/test).
    if (disabled || !siteId) return;

    // Guard 2: prevent double injection in React 18 StrictMode (double-invocation
    // of effects in dev) or if the provider is unmounted/remounted.
    if (document.getElementById('lo-script')) return;

    // Step 1 — Initialise the command queue BEFORE the async <script> tag is
    // appended. This guarantees that any commands pushed between now and script
    // load are captured and replayed in order, with no dropped events.
    window.LOQ = window.LOQ || [];

    // Step 2 — Register a 'ready' callback to capture the LO instance reference
    // and flip our isReady flag.  This is the ONLY place we interact with
    // window.LO directly; everywhere else uses the queue.
    window.LOQ.push([
      'ready',
      (lo) => {
        loRef.current = lo;
        setIsReady(true);
      },
    ]);

    // Step 3 — Append the async, deferred script tag.  Lucky Orange will drain
    // window.LOQ once the script initialises and will continue processing
    // subsequent pushes synchronously from that point.
    const script = document.createElement('script');
    script.id = 'lo-script';
    // script.src = `https://d.luckyorange.net/core.js?site-id=${siteId}`;
    script.src = `https://tools.luckyorange.com/core/lo.js?site-id=${siteId}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // No cleanup: the script and the global queue intentionally outlive any
    // individual React subtree to provide site-wide analytics continuity.
  }, [siteId, disabled]);

  // ── identifyApplication ─────────────────────────────────────────────────────
  const identifyApplication = useCallback((applicationId: string) => {
    if (!applicationId) return;

    /**
     * Closure that performs the actual identify call.
     * Wrapped in a function so it can be both:
     *   a) Queued via LOQ if LO is not yet loaded.
     *   b) Called directly if LO is already available.
     *
     * IMPORTANT: Only `applicationId` is passed — never name, email, or
     * any other PII.  The identify call maps session → database record
     * purely by the internal ID so HR can correlate recordings with
     * applications without exposing applicant personal data.
     */
    // Persist the ID so every subsequent trackMilestone call includes it
    // automatically — callers no longer need to pass it explicitly.
    currentApplicationIdRef.current = applicationId;

    /**
     * Executes the identify call against whichever API surface is available.
     * Tries the argument `lo` first, then falls back to window.LO because
     * Lucky Orange attaches visitor/events sub-modules to window.LO
     * asynchronously after the 'ready' callback fires.
     */
    const applyIdentify = (lo: LOInstance) => {
      const api = lo?.visitor ?? window.LO?.visitor;
      api?.identify?.(applicationId, { applicationId });
    };

    if (window.LO?.visitor?.identify) {
      // Sub-module already attached — call synchronously.
      applyIdentify(window.LO);
    } else {
      // Still loading — queue; Lucky Orange replays on initialisation.
      window.LOQ = window.LOQ || [];
      window.LOQ.push(['ready', applyIdentify]);
    }
  }, []);

  // ── trackMilestone ──────────────────────────────────────────────────────────
  const trackMilestone = useCallback(
    (eventName: EventName, metadata: Record<string, unknown> = {}) => {
      /**
       * Root cause of the "Cannot read properties of undefined (reading 'track')"
       * error: Lucky Orange fires the 'ready' callback while still attaching
       * sub-modules (visitor, events) to the LO object.  The `lo` argument and
       * window.LO may therefore both be partially initialised at callback time.
       *
       * Strategy:
       *  1. If window.LO.events.track is already available, call it directly —
       *     this is the fastest and safest path after the script is fully loaded.
       *  2. Otherwise push onto LOQ with a doubly-guarded callback: try `lo.events`
       *     first, then `window.LO.events` as a fallback.  Optional-chain (?.) and
       *     optional-call (?.) prevent any throw if either is still undefined.
       */
      const payload: Record<string, unknown> = {
        timestamp: new Date().toISOString(),
        ...(currentApplicationIdRef.current
          ? { applicationId: currentApplicationIdRef.current }
          : {}),
        ...metadata,
      };

      if (window.LO?.events?.track) {
        // Fast path: LO fully loaded — skip the queue entirely.
        window.LO.events.track(eventName, payload);
        return;
      }

      // Slow path: LO still loading or events not yet attached.
      window.LOQ = window.LOQ || [];
      window.LOQ.push([
        'ready',
        (lo) => {
          // Prefer the argument; fall back to window.LO which may be
          // more complete by the time this callback actually executes.
          const api = lo?.events ?? window.LO?.events;
          api?.track?.(eventName, payload);
        },
      ]);
    },
    [],
  );

  // ── Context value ───────────────────────────────────────────────────────────
  const value: LuckyOrangeContextValue = {
    isReady,
    identifyApplication,
    trackMilestone,
  };

  return (
    <LuckyOrangeContext.Provider value={value}>
      {children}
    </LuckyOrangeContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// useLuckyOrange hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides access to the Lucky Orange analytics API anywhere in the
 * component tree wrapped by <LuckyOrangeProvider>.
 *
 * @example
 * ```tsx
 * const { trackMilestone, identifyApplication } = useLuckyOrange();
 *
 * // Fire a milestone
 * trackMilestone('Step Completed', { step: 2, stepName: 'Address Details' });
 *
 * // Identify the session once you have an Application ID
 * identifyApplication(applicationId);
 * ```
 *
 * @throws If used outside of a <LuckyOrangeProvider> subtree.
 */
export const useLuckyOrange = (): LuckyOrangeContextValue => {
  const ctx = useContext(LuckyOrangeContext);
  if (!ctx) {
    throw new Error(
      'useLuckyOrange must be called inside a <LuckyOrangeProvider>. ' +
        'Wrap your application root (or the relevant subtree) with ' +
        '<LuckyOrangeProvider siteId="YOUR_SITE_ID">.',
    );
  }
  return ctx;
};

export default LuckyOrangeProvider;
